import { supabase } from '../lib/supabaseClient'

export type DeleteAccountResult =
  | { success: true }
  | { success: false; error: string }

const UNCONFIRMED_MESSAGE =
  'We couldn’t confirm that your account was deleted. Please try again or contact support.'

function messageFromBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const record = body as Record<string, unknown>
  const candidate = record.error ?? record.message
  if (typeof candidate === 'string' && candidate.trim()) return candidate
  if (candidate && typeof candidate === 'object') {
    const nested = (candidate as Record<string, unknown>).message
    if (typeof nested === 'string' && nested.trim()) return nested
  }
  return null
}

async function readFunctionError(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (typeof Response !== 'undefined' && context instanceof Response) {
    try {
      const body: unknown = await context.clone().json()
      const message = messageFromBody(body)
      if (message) return message
    } catch {
      try {
        const text = await context.text()
        if (text.trim()) return text
      } catch {
        /* fall through */
      }
    }
  }
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong while deleting your account. Please try again.'
}

/**
 * Deletes the signed-in user's account via the server-side `delete-account`
 * edge function. The client never deletes rows or files directly.
 * Success is only reported when the function responds with `success: true`.
 */
export async function deleteAccount(): Promise<DeleteAccountResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session) {
    return {
      success: false,
      error: 'Your session has expired. Please sign in again, then retry.',
    }
  }
  try {
    const { data, error } = await supabase.functions.invoke('delete-account', {
      body: { confirm: 'DELETE' },
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (error) return { success: false, error: await readFunctionError(error) }
    if (data && typeof data === 'object' && (data as { success?: unknown }).success === true) {
      return { success: true }
    }
    return { success: false, error: messageFromBody(data) ?? UNCONFIRMED_MESSAGE }
  } catch (err) {
    return { success: false, error: await readFunctionError(err) }
  }
}

/**
 * Check if user has active subscription before deletion
 */
export async function checkActiveSubscription(
  userId: string,
): Promise<boolean> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    const subscriptionPlan = user?.user_metadata?.subscriptionPlan || 'free'
    // Any plan other than the free tier represents a paid commitment.
    return subscriptionPlan !== 'free'
  } catch (error) {
    console.error('Error checking subscription:', error)
    return false
  }
}
